/**
 * SKILLCARDS qui progressent : créature origami dans la teinte de la carte,
 * titre, « +N XP » (« Carte complète » si l'élève a déjà atteint l'XP max).
 * - `CardXpGain` : popup « Bonne réponse » (une question).
 * - `CardXpGainList` : bilan de fin d'exercice (cumul de la tentative).
 * L'XP est versée à la carte à la validation (`finish_attempt`).
 */

import { OrigamiCreature } from '@jeraisonne/ui/tool-cards';
import { cardPalette } from '@jeraisonne/db/cards';
import {
  linkQuestionXp,
  useStudentSkillcards,
  type CardXpGainItem,
  type ExerciseCardLink,
} from '@/lib/cards/student-skillcards';
import { MathText } from '@jeraisonne/ui';
import { CountUpNumber } from '@/components/count-up-number';

export function CardXpGainList({ items }: { items: CardXpGainItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="space-y-2" aria-label="SKILLCARDS qui progressent">
      {items.map(({ card: c, gain, complete, fromScratch }) => {
        const palette = cardPalette(c.levels, c.color_theme);
        return (
          <li
            key={c.card_id}
            className="flex items-center gap-3 rounded-2xl p-2 text-left"
            style={{ background: palette.bg }}
          >
            <span
              aria-hidden
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2"
              style={{ borderColor: palette.accent }}
            >
              <OrigamiCreature
                creature={c.creature}
                accent={palette.accent}
                className="h-10 w-10"
              />
            </span>
            <span className="min-w-0 flex-1 text-sm font-bold text-white">
              <span className="block truncate">
                <MathText>{c.title}</MathText>
              </span>
              {!!fromScratch && !complete && (
                <span className="block text-xs font-semibold opacity-80">
                  dont +{fromScratch} HORA de ton brouillon
                </span>
              )}
            </span>
            <span
              className="shrink-0 rounded-full px-2.5 py-1 text-xs font-extrabold"
              style={{ background: palette.accent, color: palette.bg }}
            >
              {complete ? (
                'Carte complète'
              ) : (
                <>
                  +<CountUpNumber value={gain} delay={450} /> HORA
                </>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function CardXpGain({ cards, userId }: { cards: ExerciseCardLink[]; userId: string }) {
  const mine = useStudentSkillcards(cards.length > 0 ? userId : undefined);
  if (cards.length === 0) return null;
  return (
    <CardXpGainList
      items={cards.map((c) => {
        const xp = mine.data?.find((m) => m.id === c.card_id)?.xp ?? 0;
        const remaining = Math.max(0, c.xp_max - xp);
        return { card: c, gain: Math.min(remaining, linkQuestionXp(c)), complete: remaining === 0 };
      })}
    />
  );
}
